import * as React from "react";
import * as ReactDOM from "react-dom";

import { arrayIsNotEmpty, reject } from "../../../../../../Common/TypingUtils";
import { DomElementHack } from "../../../Commons/DOM/DomElementHack";
import { ADD_UNIT_CLICK } from "../../../metricsAction";
import { AbsoluteResolvedModelPath } from "../../../../../../Common/ModelPath/ModelPath";
import { IUnitController, IUnitRegistry } from "../../Navigation/UnitRegistry/IUnitRegistry";
import {
    BottomUnitsNavigatorContainer,
    TopUnitsNavigatorContainer,
    UnitAdderContainer,
} from "../../Navigation/UnitsLayout/UnitsLayout";
import { useMetrics } from "../../../Internals/Metrics/MetricsProviderContext";
import { TestPageObjectHandle } from "../../../Services/TestPageObjects/TestPageObjectHandle";
import { toHtmlElementOrNull } from "../../../Commons/DOM/DomHelpers";
import { UnitLayoutKind } from "../UnitLayoutKind";
import { MultipleUnitContainer } from "../MultipleUnit/MultipleUnitContainer";
import { UnitId, unitIdToLegacyUnitId, unitIdWithInstance } from "../UnitId/UnitId";
import { UnitIdProvider } from "../UnitIdContext";
import { UnitAdder } from "../Internals/UnitAdder/UnitAdder";
import { MultipleUnitDefaultContent } from "../MultipleUnitDefaultContent";
import { MultipleUnitsNavigator } from "../MultipleUnitsNavigator/MultipleUnitsNavigator";
import { ModalMultipleUnitInstanceContainer } from "../Internals/ModalMultipleUnitInstance/ModalMultipleUnitInstanceContainer";
import { PushInstance } from "../../../Internals/ControlContexts/PathContexts/PushInstance";
import { FocusManagementObserver } from "../../../Services/FocusManagement/FocusManagementObserver";
import { ClearPageMethod } from "../../../../Engine/ActionCreators/UnitActionCreators";
import { ChildPage, UnitClearConfirmationSettings } from "../Unit";
import { UnitIdImmutableSet } from "../UnitId/UnitIdImmutableSet";
import { PropsPageActions } from "../Types";
import { PageAction } from "../Internals/PageActions";
import { DisableUnitActionsProvider } from "../DisableUnitActionsContext";
import { ActionName } from "../MultipleUnit/MultipleUnit";

import { pagingForUnitsCount as defaultPagingForUnitsCount } from "./PagingForUnitsCount";

export interface MultipleUnitsProps {
    noContent: boolean;
    unitId: UnitId;
    name?: string;
    bindingPath: AbsoluteResolvedModelPath;
    unitRegistry: IUnitRegistry;
    children?: React.ReactNode;
    layoutKind: UnitLayoutKind;
    height?: number;
    hidden?: boolean;
    minHeight?: number;
    instances: readonly string[];
    pages: ChildPage[];
    clearConfirmationSettings: UnitClearConfirmationSettings;
    currentInstance: undefined | string;
    maxWidth?: number;
    optional?: boolean;
    hasDefaultContent: boolean;
    pageActions: PropsPageActions;
    hideEnableAutoValues?: boolean;
    enableHorizontalScrolling?: boolean;
    onHighlightUnit: (unitId: UnitId) => void;
    unitsWidth: { [key: string]: { width: number } };
    updateEmptyUnits: (unitId: UnitId, empty: boolean) => void;
    onAddMultipleUnitInstance: (newIndex?: number) => void;
    onRemoveMultipleUnitInstance: (instance: string, id: UnitId) => void;
    onCopyMultipleUnitInstance: (instance: string, id: UnitId) => Promise<void>;
    onEnableAutoValues?: (instance: string) => void;
    onClearMultipleUnit?: (instanceId: string, method: ClearPageMethod) => void;
    showUnitBorder: boolean;
    modalUnitId?: UnitId;
    modalUnitTargetInstance?: string;
    isReadOnly?: boolean;
    unitsCountForPaging?: number;
    maxOccurs?: undefined | number;
    onMount: () => void;
    onUnmount: () => void;
    emptyUnits: UnitIdImmutableSet;
    extraPageActions?: PageAction[];
    excludedPageActions?: ActionName[];
    modalTitle?: string;
}

export function MultipleUnits(props: MultipleUnitsProps): JSX.Element {
    const track = useMetrics();
    const multipleUnit = React.useRef<HTMLElement | null>(null);
    const legacyUnitId = React.useMemo(() => unitIdToLegacyUnitId(props.unitId), [props.unitId]);
    const [currentInstanceIndex, setCurrentInstanceIndex] = React.useState<number | undefined>(undefined);
    const instances = props.instances;
    const unitsCountForPaging = props.unitsCountForPaging ?? defaultPagingForUnitsCount;

    const handleFocusRequest = (): void => {
        // Открыть карточку, если снавигировались извне
        if (props.modalUnitTargetInstance && !isNaN(Number(props.modalUnitTargetInstance))) {
            setCurrentInstanceIndex(Number(props.modalUnitTargetInstance));
        }
        if (multipleUnit.current != undefined) {
            multipleUnit.current.scrollIntoView({ block: "start", inline: "start" });
        }
    };

    const handleEnsureUnitAvailableForScroll = React.useCallback(
        (targetUnit: UnitId) => {
            if (targetUnit.instance != undefined) {
                const index = instances.indexOf(targetUnit.instance);
                if (index >= 0) {
                    setCurrentInstanceIndex(index);
                }
            }
        },
        [instances]
    );

    const ensureUnitAvailableForScrollRef = React.useRef(handleEnsureUnitAvailableForScroll);

    const prevInstancesCountRef = React.useRef(props.instances.length);

    React.useEffect(() => {
        const prevCount = prevInstancesCountRef.current;
        const currentCount = props.instances.length;

        if (currentCount > prevCount) {
            props.updateEmptyUnits(props.unitId, false);
        } else if (currentCount < prevCount && currentCount === 0) {
            props.updateEmptyUnits(props.unitId, true);
        }

        prevInstancesCountRef.current = currentCount;
    }, [props.instances.length, props.unitId, props.updateEmptyUnits]);

    React.useEffect(() => {
        ensureUnitAvailableForScrollRef.current = handleEnsureUnitAvailableForScroll;
    }, [handleEnsureUnitAvailableForScroll]);

    const controller = React.useMemo<IUnitController>(
        () => ({
            getRootElement: function (): HTMLElement | undefined {
                return multipleUnit.current ?? undefined;
            },
            ensureUnitAvailableForScroll: (targetUnit: UnitId) => {
                ensureUnitAvailableForScrollRef.current?.(targetUnit);
            },
            scrollIntoView: function () {
                multipleUnit.current?.scrollIntoView({ block: "start", inline: "start" });
            },
        }),
        []
    );

    React.useEffect(() => {
        if (props.optional && props.instances.length === 0) {
            props.updateEmptyUnits(props.unitId, true);
        } else {
            props.updateEmptyUnits(props.unitId, false);
        }
    }, [props.emptyUnits]);

    React.useEffect(() => {
        props.unitRegistry.registerUnit(props.unitId, controller);
        return () => props.unitRegistry.unregisterUnit(props.unitId);
    }, [legacyUnitId]);

    React.useEffect(() => {
        if (instances.length <= unitsCountForPaging) {
            return;
        }
        if (props.currentInstance != undefined) {
            const index = instances.indexOf(props.currentInstance);
            if (index >= 0) {
                setCurrentInstanceIndex(index);
            }
        }
    }, [props.currentInstance]);

    const handleClearMultipleUnitInstance = React.useCallback((instanceId: string, method: ClearPageMethod) => {
        props.onClearMultipleUnit?.(instanceId, method);
    }, []);

    const handleInsertMultipleUnitInstance = React.useCallback(
        (newIndex?: number): void => {
            track(ADD_UNIT_CLICK, { unit: legacyUnitId });
            if (props.bindingPath != undefined) {
                props.onAddMultipleUnitInstance(newIndex);
            }
        },
        [legacyUnitId]
    );

    const disableAppend = props.maxOccurs != undefined && instances.length >= props.maxOccurs;
    const showAppendUnitButton = !props.pageActions.hideButton;
    const showInsertUnitButton = !props.pageActions.hideButton && props.pageActions.showInsertButtons;
    const unitName = props.name || "раздел";

    const testPageObject = React.useMemo(
        () => ({
            getType: () => "MultipleUnits",
            add: handleInsertMultipleUnitInstance,
            getUnitCount: () => props.instances?.length ?? 0,
            getCurrentInstance: () => currentInstanceIndex,
            deleteInstance: (instance: string) => props.onRemoveMultipleUnitInstance(instance, props.unitId),
            getInstances: () => props.instances,
        }),
        [handleInsertMultipleUnitInstance, props.instances, currentInstanceIndex]
    );

    const handleChangeInstanceIndex = (nextIndex: number) => {
        const instance = instances[nextIndex];
        if (instance != undefined) {
            props.onHighlightUnit(unitIdWithInstance(props.unitId, instance));
        }
        setCurrentInstanceIndex(nextIndex);
    };

    if (!arrayIsNotEmpty(instances) && props.hasDefaultContent) {
        return props.isReadOnly ? (
            <></>
        ) : (
            <FocusManagementObserver onRequestFocus={handleFocusRequest}>
                <TestPageObjectHandle id={[props.bindingPath, props.unitId.pageId]} pageObject={testPageObject}>
                    <UnitIdProvider id={props.unitId}>
                        <DomElementHack
                            ref={x => (multipleUnit.current = toHtmlElementOrNull(ReactDOM.findDOMNode(x)))}>
                            <MultipleUnitDefaultContent
                                onAddInstance={() => handleInsertMultipleUnitInstance(0)}
                                showBorder={props.showUnitBorder}
                                minHeight={props.height}
                                onHighlightUnit={props.onHighlightUnit}
                                unitId={props.unitId}>
                                {props.children}
                            </MultipleUnitDefaultContent>
                        </DomElementHack>
                    </UnitIdProvider>
                </TestPageObjectHandle>
            </FocusManagementObserver>
        );
    }

    const getMultipleUnitContainer = (instance: string, index: number) => (
        <DisableUnitActionsProvider>
            <MultipleUnitContainer
                name={props.name}
                enableHorizontalScrolling={props.enableHorizontalScrolling ?? false}
                bindingPath={props.bindingPath ?? reject()}
                height={props.height}
                index={index}
                parentUnitId={props.unitId}
                instance={instance}
                disableAppend={disableAppend}
                layoutKind={props.layoutKind}
                pageActions={props.pageActions}
                pages={props.pages}
                clearConfirmationSettings={props.clearConfirmationSettings}
                hideEnableAutoValues={props.hideEnableAutoValues}
                allowRemove={props.optional ? true : instances.length > 1}
                onHighlightUnit={props.onHighlightUnit}
                onRemoveUnit={props.onRemoveMultipleUnitInstance}
                onCopyUnit={props.onCopyMultipleUnitInstance}
                onEnableAutoValues={props.onEnableAutoValues}
                showUnitBorder={props.showUnitBorder}
                unitRegistry={props.unitRegistry}
                noContent={props.noContent}
                maxOccurs={props.maxOccurs}
                onClearUnit={handleClearMultipleUnitInstance}
                extraPageActions={props.extraPageActions}
                excludedPageActions={props.excludedPageActions}
                modalTitle={props.modalTitle}
                onMount={props.onMount}
                onUnmount={props.onUnmount}>
                {props.children}
            </MultipleUnitContainer>
        </DisableUnitActionsProvider>
    );

    const getInstanceContent = (instance: string, instanceIndex: number) => {
        const bindingPath = props.bindingPath;
        if (instance !== props.modalUnitTargetInstance) {
            return getMultipleUnitContainer(instance, instanceIndex);
        } else {
            const modalUnitId = props.modalUnitId ?? reject();
            return (
                <PushInstance bindingPath={bindingPath} instance={props.modalUnitTargetInstance}>
                    <UnitIdProvider id={modalUnitId}>
                        <ModalMultipleUnitInstanceContainer
                            key={unitIdToLegacyUnitId(modalUnitId)}
                            unitId={modalUnitId}
                            pages={props.pages}
                            clearConfirmationSettings={props.clearConfirmationSettings}
                            showUnitBorder={props.showUnitBorder}
                            unitRegistry={props.unitRegistry}
                        />
                    </UnitIdProvider>
                </PushInstance>
            );
        }
    };

    if (instances.length > unitsCountForPaging) {
        const instance = instances[currentInstanceIndex ?? 0] ?? instances[instances.length - 1] ?? reject();
        const index = currentInstanceIndex ?? 0;
        const instanceLegacyUnitId = unitIdToLegacyUnitId({
            pageId: props.unitId.pageId,
            instance: instance,
        });
        const content = getInstanceContent(instance, index);
        return (
            <FocusManagementObserver onRequestFocus={handleFocusRequest}>
                <TestPageObjectHandle id={[props.bindingPath, props.unitId.pageId]} pageObject={testPageObject}>
                    <UnitIdProvider id={props.unitId}>
                        <DomElementHack
                            key={instanceLegacyUnitId + "Unit" + instance}
                            ref={x => (multipleUnit.current = toHtmlElementOrNull(ReactDOM.findDOMNode(x)))}>
                            <TopUnitsNavigatorContainer>
                                <MultipleUnitsNavigator
                                    currentIndex={currentInstanceIndex ?? 0}
                                    onChangeIndex={handleChangeInstanceIndex}
                                    totalCount={instances.length}
                                    title={props.name}
                                />
                            </TopUnitsNavigatorContainer>
                            {content}
                            {showAppendUnitButton && !props.isReadOnly && (
                                <UnitAdderContainer>
                                    <UnitAdder
                                        data-tid="AppendUnitButton"
                                        style={{ marginRight: "auto", marginLeft: "auto" }}
                                        onClick={() => handleInsertMultipleUnitInstance(instances.length)}
                                        unitName={unitName}
                                        text={props.pageActions.lastButtonName}
                                        disableAppend={disableAppend}
                                    />
                                </UnitAdderContainer>
                            )}
                            <BottomUnitsNavigatorContainer>
                                <MultipleUnitsNavigator
                                    currentIndex={currentInstanceIndex ?? 0}
                                    onChangeIndex={handleChangeInstanceIndex}
                                    totalCount={instances.length}
                                    title={props.name}
                                />
                            </BottomUnitsNavigatorContainer>
                        </DomElementHack>
                    </UnitIdProvider>
                </TestPageObjectHandle>
            </FocusManagementObserver>
        );
    }

    return (
        <FocusManagementObserver onRequestFocus={handleFocusRequest}>
            <TestPageObjectHandle id={[props.bindingPath, props.unitId.pageId]} pageObject={testPageObject}>
                <UnitIdProvider id={props.unitId}>
                    <DomElementHack ref={x => (multipleUnit.current = toHtmlElementOrNull(ReactDOM.findDOMNode(x)))}>
                        {instances.map((instance, index) => {
                            const instanceLegacyUnitId = unitIdToLegacyUnitId({
                                pageId: props.unitId.pageId,
                                instance: instance,
                            });
                            const isLastItem = index === instances.length - 1;
                            const content = getInstanceContent(instance, index);
                            return (
                                <React.Fragment key={instanceLegacyUnitId + "Unit" + instance}>
                                    {content}
                                    {!isLastItem && showInsertUnitButton && !props.isReadOnly && (
                                        <UnitAdderContainer>
                                            <UnitAdder
                                                style={{ marginRight: "auto", marginLeft: "auto" }}
                                                data-tid="InsertUnitButton"
                                                onClick={() => handleInsertMultipleUnitInstance(index + 1)}
                                                unitName={unitName}
                                                text={props.pageActions.intermediateButtonName}
                                                disableAppend={disableAppend}
                                            />
                                        </UnitAdderContainer>
                                    )}
                                </React.Fragment>
                            );
                        })}
                        {showAppendUnitButton && !props.isReadOnly && (
                            <UnitAdderContainer>
                                <UnitAdder
                                    style={{ marginRight: "auto", marginLeft: "auto" }}
                                    data-tid="AppendUnitButton"
                                    onClick={() => handleInsertMultipleUnitInstance(instances.length)}
                                    unitName={unitName}
                                    text={props.pageActions.lastButtonName}
                                    disableAppend={disableAppend}
                                />
                            </UnitAdderContainer>
                        )}
                    </DomElementHack>
                </UnitIdProvider>
            </TestPageObjectHandle>
        </FocusManagementObserver>
    );
}
